/**
 * 时间工具类 - 支持乌兹别克斯坦时间与中国时间互转
 */
export default {
    // 时区常量
    TIMEZONES: {
        UZBEKISTAN: 'Asia/Tashkent',  // UTC+5
        CHINA: 'Asia/Shanghai'        // UTC+8
    },

    /**
     * 通用时间格式化方法
     * @param {string|Date} time 时间(ISO字符串或Date对象)
     * @param {string} timeZone 目标时区
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的时间字符串
     */
    formatTime(time, timeZone, options = {}) {
        const date = new Date(time);
        const defaultOptions = {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            ...options
        };

        const formatter = new Intl.DateTimeFormat('en-GB', defaultOptions);
        const parts = formatter.formatToParts(date);

        // 手动构建 YYYY-MM-DD HH:MM:SS 格式
        const year = parts.find(p => p.type === 'year').value;
        const month = parts.find(p => p.type === 'month').value;
        const day = parts.find(p => p.type === 'day').value;
        const hour = parts.find(p => p.type === 'hour').value;
        const minute = parts.find(p => p.type === 'minute').value;
        const second = parts.find(p => p.type === 'second').value;

        return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },

    /**
     * 将时间转换为指定时区的Date对象
     * @param {string|Date} time 时间
     * @param {string} targetTimeZone 目标时区
     * @returns {Date} 目标时区的Date对象
     */
    convertToTimezoneDate(time, targetTimeZone) {
        const date = new Date(time);
        // 使用toLocaleString来获取目标时区的时间字符串
        const str = date.toLocaleString('en-US', { timeZone: targetTimeZone });
        return new Date(str);
    },

    /******************** 乌兹别克斯坦时间相关 ********************/

    /**
     * 将时间转换为乌兹别克斯坦时间并格式化
     * @param {string|Date} time 时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的乌兹别克斯坦时间
     */
    toUzbekistanTime(time, options = {}) {
        return this.formatTime(time, this.TIMEZONES.UZBEKISTAN, options);
    },

    /**
     * 将时间转换为乌兹别克斯坦时间(Date对象)
     * @param {string|Date} time 时间
     * @returns {Date} 乌兹别克斯坦时间的Date对象
     */
    toUzbekistanDate(time) {
        return this.convertToTimezoneDate(time, this.TIMEZONES.UZBEKISTAN);
    },

    /**
     * 获取当前乌兹别克斯坦时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的当前乌兹别克斯坦时间
     */
    getCurrentUzbekistanTime(options = {}) {
        return this.toUzbekistanTime(new Date(), options);
    },

    /******************** 中国时间相关 ********************/

    /**
     * 将时间转换为中国时间并格式化
     * @param {string|Date} time 时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的中国时间
     */
    toChinaTime(time, options = {}) {
        return this.formatTime(time, this.TIMEZONES.CHINA, options);
    },

    /**
     * 将时间转换为中国时间(Date对象)
     * @param {string|Date} time 时间
     * @returns {Date} 中国时间的Date对象
     */
    toChinaDate(time) {
        return this.convertToTimezoneDate(time, this.TIMEZONES.CHINA);
    },

    /**
     * 获取当前中国时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的当前中国时间
     */
    getCurrentChinaTime(options = {}) {
        return this.toChinaTime(new Date(), options);
    },

    /******************** 时间互转相关 ********************/

    /**
     * 乌兹别克斯坦时间转中国时间
     * @param {string|Date} uzbekistanTime 乌兹别克斯坦时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的中国时间
     */
    uzbekistanToChina(uzbekistanTime, options = {}) {
        // 先转换为乌兹别克斯坦时区的Date对象
        const uzDate = this.convertToTimezoneDate(uzbekistanTime, this.TIMEZONES.UZBEKISTAN);
        // 然后格式化为中国时间
        return this.formatTime(uzDate, this.TIMEZONES.CHINA, options);
    },

    /**
     * 中国时间转乌兹别克斯坦时间
     * @param {string|Date} chinaTime 中国时间
     * @param {Object} options 格式化选项
     * @returns {string} 格式化后的乌兹别克斯坦时间
     */
    chinaToUzbekistan(chinaTime, options = {}) {
        // 先转换为中国时区的Date对象
        const cnDate = this.convertToTimezoneDate(chinaTime, this.TIMEZONES.CHINA);
        // 然后格式化为乌兹别克斯坦时间
        return this.formatTime(cnDate, this.TIMEZONES.UZBEKISTAN, options);
    },

    /**
     * 计算两个时区的时间差（中国比乌兹别克斯坦快3小时）
     * @returns {number} 小时差
     */
    getTimezoneDifference() {
        return 3; // 中国(UTC+8) - 乌兹别克斯坦(UTC+5) = 3小时
    }
};
